import { useEffect, useState } from "react";
import { listTestimonials, createTestimonial, updateTestimonial, deleteTestimonial, fileUrl } from "../lib/api";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { FileUploader } from "./FileUploader";
import { Stars } from "./TestimonialsSection";
import { toast } from "sonner";
import { Trash2, Edit, Plus, Save, X, Star, MapPin, Zap } from "lucide-react";

const EMPTY = { name: "", location: "", rating: 5, text: "", system_kw: "", photo_url: "" };

export function AdminReviews() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);

  const load = () => listTestimonials().then(setItems).catch(() => toast.error("Failed to load reviews"));
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const startEdit = (t) => {
    setEditingId(t.id);
    setForm({ name: t.name, location: t.location || "", rating: t.rating, text: t.text, system_kw: t.system_kw || "", photo_url: t.photo_url || "" });
  };
  const cancel = () => { setEditingId(null); setForm(EMPTY); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.text.trim()) return toast.error("Name and review text are required");
    const payload = { ...form, rating: Number(form.rating) };
    try {
      if (editingId) {
        await updateTestimonial(editingId, payload);
        toast.success("Review updated");
      } else {
        await createTestimonial(payload);
        toast.success("Review added");
      }
      cancel();
      load();
    } catch {
      toast.error("Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this review?")) return;
    try {
      await deleteTestimonial(id);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-8">
      <form onSubmit={submit} data-testid="review-form" className="lg:col-span-2 min-w-0 bg-white border border-border rounded-2xl p-4 sm:p-6 space-y-4 h-fit lg:sticky lg:top-24">
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-xl font-bold">{editingId ? "Edit review" : "Add customer review"}</h3>
          {editingId && (
            <button type="button" onClick={cancel} data-testid="cancel-review-edit" className="text-muted-foreground hover:text-secondary">
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
        <div>
          <Label className="text-sm font-semibold mb-2 block">Customer name *</Label>
          <Input value={form.name} onChange={set("name")} data-testid="rf-name" placeholder="e.g. Ramesh Deshmukh" className="bg-white border-border rounded-lg h-11" />
        </div>
        <div>
          <Label className="text-sm font-semibold mb-2 block">Rating *</Label>
          <div className="flex gap-1" data-testid="rf-rating">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setForm({ ...form, rating: n })}
                data-testid={`rf-star-${n}`}
                aria-label={`${n} star${n > 1 ? "s" : ""}`}
                className="p-1 rounded hover:scale-110 transition-transform"
              >
                <Star className={`w-7 h-7 ${n <= form.rating ? "fill-primary text-primary" : "text-border"}`} strokeWidth={1.5} />
              </button>
            ))}
          </div>
        </div>
        <div>
          <Label className="text-sm font-semibold mb-2 block">Review *</Label>
          <Textarea value={form.text} onChange={set("text")} data-testid="rf-text" rows={4} placeholder="What did the customer say?" className="bg-white border-border rounded-lg" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-sm font-semibold mb-2 block">Location</Label>
            <Input value={form.location} onChange={set("location")} data-testid="rf-location" placeholder="Akola" className="bg-white border-border rounded-lg h-11" />
          </div>
          <div>
            <Label className="text-sm font-semibold mb-2 block">System installed</Label>
            <Input value={form.system_kw} onChange={set("system_kw")} data-testid="rf-system" placeholder="5 kW Rooftop" className="bg-white border-border rounded-lg h-11" />
          </div>
        </div>
        <div>
          <Label className="text-sm font-semibold mb-2 block">Customer photo (optional)</Label>
          <FileUploader kind="image" value={form.photo_url} onChange={(url) => setForm({ ...form, photo_url: url })} testid="rf-photo-upload" />
        </div>
        <button type="submit" data-testid="rf-submit" className="w-full inline-flex items-center justify-center gap-2 bg-primary text-white hover:bg-orange-600 transition-colors font-medium rounded-full px-8 py-3">
          {editingId ? <><Save className="w-4 h-4" /> Save changes</> : <><Plus className="w-4 h-4" /> Add review</>}
        </button>
      </form>

      <div className="lg:col-span-3 min-w-0 space-y-4">
        {items.length === 0 && (
          <div className="bg-white border border-border rounded-2xl p-12 text-center text-muted-foreground">
            No reviews yet. Add your first customer review.
          </div>
        )}
        {items.map((t) => (
          <div key={t.id} data-testid={`admin-review-${t.id}`} className="bg-white border border-border rounded-2xl p-5 flex gap-4">
            {t.photo_url ? (
              <img src={fileUrl(t.photo_url)} alt={t.name} className="w-14 h-14 rounded-full object-cover shrink-0" />
            ) : (
              <div className="w-14 h-14 rounded-full bg-secondary text-white flex items-center justify-center font-heading font-bold shrink-0">
                {t.name.split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase()}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-3">
                <div className="font-heading text-lg font-semibold leading-tight">{t.name}</div>
                <Stars rating={t.rating} className="w-3.5 h-3.5" />
              </div>
              <div className="text-sm text-muted-foreground line-clamp-2 mt-1">{t.text}</div>
              <div className="flex flex-wrap gap-4 text-xs text-secondary mt-2">
                {t.location && <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-primary" /> {t.location}</span>}
                {t.system_kw && <span className="inline-flex items-center gap-1.5"><Zap className="w-3.5 h-3.5 text-primary" /> {t.system_kw}</span>}
              </div>
            </div>
            <div className="flex flex-col gap-2 shrink-0">
              <button onClick={() => startEdit(t)} data-testid={`edit-review-${t.id}`} title="Edit" className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-border hover:border-secondary text-secondary">
                <Edit className="w-4 h-4" />
              </button>
              <button onClick={() => remove(t.id)} data-testid={`delete-review-${t.id}`} title="Delete" className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-border hover:border-destructive text-destructive">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
